/**
 * Animation configuration for Quickstart flows
 * Uses Framer Motion for smooth transitions
 */

/**
 * Global animation enable/disable flag
 * Set to false to disable all animations
 */
export const ANIMATIONS_ENABLED = true;

export type AnimationSpeed = 'extrafast' | 'fast' | 'normal' | 'slow';
export type AnimationType = 'fade' | 'slide' | 'scale' | 'none';

export interface AnimationConfig {
  type: AnimationType;
  speed: AnimationSpeed;
  direction?: 'up' | 'down' | 'left' | 'right';
}

/**
 * Duration mappings for animation speeds (in seconds)
 */
const ANIMATION_DURATIONS: Record<AnimationSpeed, number> = {
  extrafast: 0.12,
  fast: 0.2,
  normal: 0.3,
  slow: 0.5,
};

/**
 * Easing presets
 */
const EASINGS = {
  smooth: [0.4, 0.0, 0.2, 1] as const, // Material Design standard
  snappy: [0.4, 0.0, 0.6, 1] as const, // Faster exit
  bounce: [0.68, -0.55, 0.265, 1.55] as const, // Slight overshoot
};

/**
 * Get initial animation state based on config
 */
export function getInitialState(config: AnimationConfig) {
  const { type, direction = 'up' } = config;

  switch (type) {
    case 'fade':
      return { opacity: 0 };

    case 'slide':
      return {
        opacity: 0,
        x: direction === 'left' ? -20 : direction === 'right' ? 20 : 0,
        y: direction === 'up' ? 20 : direction === 'down' ? -20 : 0,
      };

    case 'scale':
      return { opacity: 0, scale: 0.95 };

    case 'none':
    default:
      return {};
  }
}

/**
 * Get animate state based on config
 */
export function getAnimateState(config: AnimationConfig) {
  const { type } = config;

  switch (type) {
    case 'fade':
      return { opacity: 1 };

    case 'slide':
      return { opacity: 1, x: 0, y: 0 };

    case 'scale':
      return { opacity: 1, scale: 1 };

    case 'none':
    default:
      return {};
  }
}

/**
 * Get exit state based on config
 */
export function getExitState(config: AnimationConfig) {
  const { type, direction = 'up' } = config;

  switch (type) {
    case 'fade':
      return { opacity: 0 };

    case 'slide':
      return {
        opacity: 0,
        x: direction === 'left' ? 20 : direction === 'right' ? -20 : 0,
        y: direction === 'up' ? -20 : direction === 'down' ? 20 : 0,
      };

    case 'scale':
      return { opacity: 0, scale: 0.95 };

    case 'none':
    default:
      return {};
  }
}

/**
 * Get transition config
 */
export function getTransition(config: AnimationConfig) {
  const { speed, type } = config;

  if (type === 'none') return {};

  return {
    duration: ANIMATION_DURATIONS[speed],
    ease: EASINGS.smooth,
  };
}

/**
 * Default animation presets
 */
export const ANIMATION_PRESETS = {
  // Default card transition
  cardDefault: {
    type: 'fade' as AnimationType,
    speed: 'extrafast' as AnimationSpeed,
  },

  // Quick fade
  quickFade: {
    type: 'fade' as AnimationType,
    speed: 'fast' as AnimationSpeed,
  },

  // Slow scale
  slowScale: {
    type: 'scale' as AnimationType,
    speed: 'slow' as AnimationSpeed,
  },

  // No animation (for debugging or accessibility)
  none: {
    type: 'none' as AnimationType,
    speed: 'fast' as AnimationSpeed,
  },
} as const;

